import type { V2 } from '../core/math';
import type { Mixer } from './mixer';
import type { OneShots } from './oneShots';
import { SFX_PRESETS, type SfxName } from './sfxPresets';
import { jitterRatio, RoundRobin } from './mix';
import { zzfxSamples } from './zzfx';

/** Takes of each preset baked per context (ZzFX randomness differs per take), round-robin without repeats. */
const TAKES = 3;
/** Pitch spread per play on top of the takes (cents): world sounds wider, menu ones tighter. */
const SPREAD_SFX = 45;
const SPREAD_UI = 20;

/**
 * Plays the ZzFX presets (sfxPresets.ts): each preset is baked to AudioBuffers on first use (a few ms of
 * JS, once) and played through `OneShots.buffer`, so the mixer's buses, volume settings, pause, iOS
 * unlock and the distance / pan / air model all apply.
 */
export class SfxBank {
  private readonly cache = new Map<SfxName, AudioBuffer[]>();
  private readonly order = new Map<SfxName, RoundRobin>();
  private cacheCtx: BaseAudioContext | null = null;

  constructor(
    private readonly mx: Mixer,
    private readonly shots: OneShots,
  ) {}

  play(name: SfxName, pos: V2 | null = null, level = 1): void {
    const ctx = this.mx.ctx;
    if (!ctx || !this.mx.running) return;
    const p = SFX_PRESETS[name];
    const takes = this.takes(ctx, name);
    let rr = this.order.get(name);
    if (!rr) this.order.set(name, (rr = new RoundRobin(takes.length)));
    const buf = takes[rr.next()]!;
    const bus = p.bus === 'ui' ? this.mx.buses.ui : this.mx.buses.sfx;
    const pp: { lp?: number; ref?: number; echo?: number } = p;
    const rate = jitterRatio(p.bus === 'ui' ? SPREAD_UI : SPREAD_SFX);
    this.shots.buffer(buf, p.bus === 'ui' ? null : pos, bus, p.gain * level, pp.lp, pp.ref, pp.echo, rate);
  }

  private takes(ctx: BaseAudioContext, name: SfxName): AudioBuffer[] {
    if (this.cacheCtx !== ctx) {
      this.cache.clear();
      this.cacheCtx = ctx;
    }
    let t = this.cache.get(name);
    if (!t) {
      t = [];
      for (let i = 0; i < TAKES; i++) {
        const s = zzfxSamples(SFX_PRESETS[name].zz, ctx.sampleRate);
        const b = ctx.createBuffer(1, Math.max(1, s.length), ctx.sampleRate);
        b.getChannelData(0).set(s);
        t.push(b);
      }
      this.cache.set(name, t);
    }
    return t;
  }
}
